/* global $ */
import React, { useState, useEffect } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Footer from '../components/Footer';

const STATUS_COLORS = {
    new: 'bg-primary',
    contacted: 'bg-info',
    follow_up: 'bg-warning',
    qualified: 'bg-success',
    lost: 'bg-danger',
    converted: 'bg-secondary',
};

function Lead() {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filters, setFilters] = useState({ name: '', phone: '', status: '', source: '', queue: '' });

    // ─── Filter & Sort ────────────────────────────────────────────────────────────

    const filtered = leads.filter(l =>
        l.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.phone?.includes(searchTerm) ||
        l.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.source?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const advFiltered = filtered.filter(l =>
        (filters.name === '' || l.name?.toLowerCase().includes(filters.name.toLowerCase())) &&
        (filters.phone === '' || l.phone?.includes(filters.phone)) &&
        (filters.status === '' || l.status === filters.status) &&
        (filters.source === '' || l.source?.toLowerCase().includes(filters.source.toLowerCase())) &&
        (filters.queue === '' || l.lead_queue?.name?.toLowerCase().includes(filters.queue.toLowerCase()))
    );

    const sorted = React.useMemo(() => {
        let s = [...advFiltered];
        if (sortConfig.key) {
            s.sort((a, b) => {
                let av = a[sortConfig.key] ?? '', bv = b[sortConfig.key] ?? '';
                if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
                if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return s;
    }, [advFiltered, sortConfig]);

    const totalItems = sorted.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentLeads = sorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key) => {
        setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    };
    const getSortIcon = (key) => sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : '';

    useEffect(() => { setCurrentPage(1); }, [searchTerm, filters]);

    // ─── API ─────────────────────────────────────────────────────────────────────

    const fetchLeads = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/leads');
            console.log('Leads API response:', res.status, res.data);

            // Handle all response shapes
            const raw = res.data;
            let list = [];

            if (Array.isArray(raw)) {
                list = raw;
            } else if (Array.isArray(raw?.data)) {
                list = raw.data;
            } else if (Array.isArray(raw?.rows)) {
                list = raw.rows;
            } else if (Array.isArray(raw?.result)) {
                list = raw.result;
            }

            console.log(`Loaded ${list.length} leads`);
            setLeads(list);
        } catch (err) {
            console.error('Leads fetch error:', err?.response?.status, err?.response?.data, err.message);
            // 404 just means empty — don't show error popup
            if (err?.response?.status === 404) {
                setLeads([]);
            } else {
                Swal.fire({ icon: 'error', title: 'Error!', text: `Failed to fetch leads: ${err?.response?.data?.message || err.message}` });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLeads(); }, []);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?', text: "You won't be able to revert this!", icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });
        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/leads/${id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchLeads();
            } catch {
                Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to delete lead' });
            }
        }
    };

    // ─── Pagination ───────────────────────────────────────────────────────────────

    const Pagination = () => {
        const max = 5;
        let start = Math.max(1, currentPage - Math.floor(max / 2));
        let end = Math.min(totalPages, start + max - 1);
        if (end - start + 1 < max) start = Math.max(1, end - max + 1);
        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems} entries</div>
                <nav><ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(1)}>First</button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
                    </li>
                    {pages.map(n => (
                        <li key={n} className={`page-item ${n === currentPage ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(n)}>{n}</button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(totalPages)}>Last</button>
                    </li>
                </ul></nav>
            </div>
        );
    };

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Leads</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <button className="btn btn-filters w-auto" onClick={() => $('#filter_inputs').toggle()}>
                                            <span className="me-2"><img src="assets/img/icons/filter-icon.svg" alt="filter" /></span>Filter
                                        </button>
                                    </li>
                                    <li>
                                        <button className="btn btn-primary" onClick={() => navigate('/add-lead')}>
                                            <i className="fa fa-plus-circle me-2"></i>Add Lead
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div id="filter_inputs" className="card filter-card" style={{ display: 'none' }}>
                        <div className="card-body pb-0">
                            <div className="row">
                                {[
                                    { label: 'Name', name: 'name', type: 'text' },
                                    { label: 'Phone', name: 'phone', type: 'text' },
                                    { label: 'Source', name: 'source', type: 'text' },
                                    { label: 'Queue', name: 'queue', type: 'text' },
                                ].map(f => (
                                    <div key={f.name} className="col-sm-6 col-md-3">
                                        <div className="input-block mb-3">
                                            <label>{f.label}</label>
                                            <input type={f.type} className="form-control" name={f.name}
                                                value={filters[f.name]}
                                                onChange={e => setFilters(p => ({ ...p, [e.target.name]: e.target.value }))}
                                                placeholder={`Filter by ${f.label.toLowerCase()}`} />
                                        </div>
                                    </div>
                                ))}
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Status</label>
                                        <select className="form-control" name="status"
                                            value={filters.status}
                                            onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}>
                                            <option value="">All</option>
                                            <option value="new">New</option>
                                            <option value="contacted">Contacted</option>
                                            <option value="follow_up">Follow Up</option>
                                            <option value="qualified">Qualified</option>
                                            <option value="lost">Lost</option>
                                            <option value="converted">Converted</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="input-block mb-3 text-end">
                                        <button className="btn btn-secondary" onClick={() => setFilters({ name: '', phone: '', status: '', source: '', queue: '' })}>
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input type="text" className="form-control form-control-sm d-inline-block" style={{ width: '200px' }}
                                                placeholder="Search leads..." value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Name{getSortIcon('name')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('phone')}>Phone{getSortIcon('phone')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('source')}>Source{getSortIcon('source')}</th>
                                                    <th>Queue</th>
                                                    <th>Assigned To</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{getSortIcon('status')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>Created{getSortIcon('createdAt')}</th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="10" className="text-center">
                                                        <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
                                                    </td></tr>
                                                ) : currentLeads.length > 0 ? currentLeads.map((lead, idx) => (
                                                    <tr key={lead.id}>
                                                        <td>{startIndex + idx + 1}</td>
                                                        <td>
                                                            <span style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                onClick={() => navigate(`/lead-details/${lead.id}`)}>
                                                                {lead.name || <span className="text-muted fst-italic">Unknown</span>}
                                                            </span>
                                                        </td>
                                                        <td><a href={`tel:${lead.phone}`}>{lead.phone || 'N/A'}</a></td>
                                                        <td>{lead.source || 'N/A'}</td>
                                                        <td>{lead.lead_queue?.name || 'N/A'}</td>
                                                        <td>{lead.assigned_id?.user_name || 'Unassigned'}</td>
                                                        <td>
                                                            <span className={`badge ${STATUS_COLORS[lead.status] || 'bg-secondary'}`}>
                                                                {lead.status?.replace('_', ' ') || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td>{lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : 'N/A'}</td>
                                                        <td className="d-flex align-items-center">
                                                            <a className="btn-action-icon bg-primary-light me-2" style={{ cursor: 'pointer' }}
                                                                onClick={() => navigate(`/lead-details/${lead.id}`)} title="View">
                                                                <i className="fa fa-eye"></i>
                                                            </a>
                                                            <a className="btn-action-icon bg-success-light me-2" style={{ cursor: 'pointer' }}
                                                                onClick={() => navigate('/add-lead', { state: { lead, isEdit: true } })} title="Edit">
                                                                <i className="fa fa-edit"></i>
                                                            </a>
                                                            <a className="btn-action-icon bg-danger-light" style={{ cursor: 'pointer' }}
                                                                onClick={() => handleDelete(lead.id)} title="Delete">
                                                                <i className="fa fa-trash-alt"></i>
                                                            </a>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan="10" className="text-center">
                                                        {searchTerm ? 'No matching leads found' : 'No leads available'}
                                                    </td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <Footer />
            </div>
        </div>
    );
}

export default Lead;